import {
  AfterViewInit,
  Component,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { CKEditorModule } from '@ckeditor/ckeditor5-angular';
import { Subject, firstValueFrom } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { ClassicEditor, EditorConfig } from 'ckeditor5';

import { LoaderService } from 'src/app/component/loader-spinner/loader.service';
import { SelectInputSearchComponent } from 'src/app/component/select-input-search/select-input-search.component';
import { DatabaseService } from 'src/app/servicios/database/database.service';
import { MessageService } from 'src/app/servicios/message/message.service';
import { TranslateService } from '@ngx-translate/core';

import { Ayuda, AyudaHTML, Lang } from '../../../models/ayuda.model';
import { HelpSection, HelpSectionsByLang } from '../../../models/help-section.model';
import { HelpSectionEditorComponent } from '../../help-section-editor/help-section-editor.component';
import { CkUploadAdapter } from '../../../utils/ckeditor-uploader';
import { buildCkeditorConfig } from '../../../utils/ckeditor.config';
import {
  alignSectionsToStructure,
  createDefaultSections,
  flattenHelpDocument,
  htmlToPlainText,
  parseHelpDocument,
  serializeHelpDocument,
  uniqueSectionId,
} from '../../../utils/help-document.util';
import {
  attachHelpEditorPasteSanitizer,
  sanitizeHelpHtml,
  sanitizeHelpHtmlFragment,
} from '../../../utils/help-html-sanitize.util';
import {
  buildHelpLangHtml,
  getModifiedHelpLangIds,
  HelpLangContentSnapshot,
} from '../../../utils/help-lang-track.util';

export type HelpDocumentUiMode = 'simple' | 'sections';

@Component({
  selector: 'app-crear-modificar-ayuda',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatDividerModule,
    MatFormFieldModule,
    MatIconModule,
    MatSelectModule,
    MatTabsModule,
    MatTooltipModule,
    TranslateModule,
    CKEditorModule,
    SelectInputSearchComponent,
    HelpSectionEditorComponent,
  ],
  templateUrl: './crear-modificar-ayuda.component.html',
  styleUrls: ['./crear-modificar-ayuda.component.scss'],
})
export class CrearModificarAyudaComponent implements OnInit, OnDestroy, AfterViewInit {

  public ayuda = inject<AyudaHTML>(MAT_DIALOG_DATA);
  private database = inject(DatabaseService);
  private formBuilder = inject(FormBuilder);
  private dialogRef = inject<MatDialogRef<CrearModificarAyudaComponent>>(MatDialogRef);
  private messageService = inject(MessageService);
  private translateService = inject(TranslateService);
  private loaderService = inject(LoaderService);
  private http = inject(HttpClient);

  private destroyed$ = new Subject<void>();

  public langs = [
    { id: 'es', label: 'español' },
    { id: 'en', label: 'ingles' },
    { id: 'fr', label: 'frances' },
    { id: 'ca', label: 'catalan' },
    { id: 'pv', label: 'Euskera' },
  ];

  public editor: any = ClassicEditor;
  public editorConfig: EditorConfig;
  public isLayoutReady = false;

  /** Modo de contenido: simple (scroll) o menú lateral por secciones */
  public documentMode: HelpDocumentUiMode = 'simple';
  public sectionsByLang: HelpSectionsByLang = {};
  public activeLangIndex = 0;
  private currentLangTabId = 'es';

  /** Idiomas en los que el usuario editó contenido (no sincronización automática de estructura) */
  private readonly dirtyLangIds = new Set<string>();

  /** HTML por idioma al abrir el diálogo (tras limpieza), para detectar cambios reales */
  private initialLangSnapshot: HelpLangContentSnapshot = { htmlByLang: {} };

  /** Metadatos al abrir (submódulo / tipo permiso) para detectar cambios sin tocar HTML */
  private initialMeta = {
    IdSubmodulo: 0,
    IdPermisoTipo: 0,
  };

  public submodulos: any[] = [];
  public submodulosFiltered: any[] = [];
  public modulos: any[] = [];
  public tiposPermiso: any[] = [];
  public tiposPermisoAyudas: any[] = [];

  public formAyuda: FormGroup = this.formBuilder.group({
    IdSubmoduloPermisoAyuda: [this.ayuda.IdSubmoduloPermisoAyuda, []],
    IdSubmodulo:             [this.ayuda.IdSubmodulo,             [Validators.required]],
    idModulo:                [this.ayuda.idModulo,                [Validators.required]],
    IdPermisoTipo:           [this.ayuda.IdPermisoTipo,           [Validators.required]],
    es: [this.ayuda.es ?? '', []],
    en: [this.ayuda.en ?? ''],
    ca: [this.ayuda.ca ?? ''],
    fr: [this.ayuda.fr ?? ''],
    pv: [this.ayuda.pv ?? ''],
  });

  public moduloControl = new FormControl();

  private translations: Record<string, string> = {};

  public expandedDialog = false;
  public OcultarMostrar = false;
  public IconoMinimizadoMaximizado = 'expand_more';
  public maximizado = false;

  @ViewChildren(HelpSectionEditorComponent)
  private sectionEditors!: QueryList<HelpSectionEditorComponent>;

  constructor() {
    this.translateService
      .get([
        'operacionExitosa',
        'mensaje_confirmacion',
        'haOcurridoUnError',
        'help-sin-idiomas-modificados',
      ])
      .subscribe((t) => (this.translations = t));
  }

  ngOnInit(): void {
    this.sanitizeLoadedAyudaContent();
    this.initSectionsFromAyuda();
    this.captureInitialLangSnapshot();
    this.captureInitialMetaSnapshot();
    this.getData();

    this.formAyuda.get('idModulo')
      .valueChanges.pipe(takeUntil(this.destroyed$))
      .subscribe((value) => {
        if (value !== null) {
          this.filterSubmodulos(value);
        }
      });

    this.formAyuda.get('IdSubmodulo')
      .valueChanges.pipe(takeUntil(this.destroyed$))
      .subscribe((value) => {
        this.syncIdPermisoTipoEnabledFromSubmodulo(value);
        if (value !== null && value !== undefined && value !== '') {
          this.tiposPermisoSubmodulo(value);
        }
      });

    this.syncIdPermisoTipoEnabledFromSubmodulo(this.formAyuda.get('IdSubmodulo')?.value);

    this.database.obtenerModulos().subscribe((modulos: any[]) => {
      this.modulos = modulos;
      this.database.obtenerSubmodulo().subscribe((submodulos: any) => {
        this.submodulos = submodulos;
        this.submodulosFiltered = this.submodulos;
        this.applyInitialSubmoduloFromData();
      });
    });
  }

  /** Preselecciona módulo y tipos de permiso al abrir desde el header (solo IdSubmodulo). */
  private applyInitialSubmoduloFromData(): void {
    if (this.ayuda.IdSubmoduloPermisoAyuda) {
      return;
    }

    const idSub = this.ayuda.IdSubmodulo ?? this.formAyuda.get('IdSubmodulo')?.value;
    if (idSub == null || idSub === '') {
      return;
    }

    const sub = this.submodulos.find(
      (s) => Number(s.idSubmodulo ?? s.IdSubmodulo) === Number(idSub),
    );
    if (!sub) {
      return;
    }

    const idModulo = sub.idModulo ?? sub.IdModulo;
    if (idModulo == null) {
      return;
    }

    this.formAyuda.patchValue({
      idModulo,
      IdSubmodulo: Number(idSub),
    });
    this.filterSubmodulos(idModulo);
    this.tiposPermisoSubmodulo(idSub);
    this.syncIdPermisoTipoEnabledFromSubmodulo(idSub);
  }

  ngAfterViewInit(): void {
    this.editorConfig = buildCkeditorConfig();
    this.isLayoutReady = true;
  }

  get activeLangId(): string {
    return this.langs[this.activeLangIndex]?.id ?? this.currentLangTabId ?? 'es';
  }

  get canSave(): boolean {
    const metaValid =
      this.formAyuda.get('IdSubmodulo')?.valid &&
      this.formAyuda.get('idModulo')?.valid &&
      this.formAyuda.get('IdPermisoTipo')?.valid;

    if (!metaValid) return false;

    return this.langs.some(({ id }) => htmlToPlainText(this.buildHtmlForLang(id)).length > 0);
  }

  private markLangDirty(langId: string): void {
    if (langId) {
      this.dirtyLangIds.add(langId);
    }
  }

  /** Snapshot tras cargar/limpiar: base para saber qué columnas cambiaron */
  private captureInitialLangSnapshot(): void {
    const htmlByLang: Record<string, string> = {};
    this.langs.forEach(({ id }) => {
      htmlByLang[id] = this.buildHtmlForLang(id);
    });
    this.initialLangSnapshot = { htmlByLang };
    this.dirtyLangIds.clear();
  }

  private captureInitialMetaSnapshot(): void {
    const raw = this.formAyuda.getRawValue();
    this.initialMeta = {
      IdSubmodulo: Number(raw.IdSubmodulo ?? this.ayuda.IdSubmodulo ?? 0),
      IdPermisoTipo: Number(raw.IdPermisoTipo ?? this.ayuda.IdPermisoTipo ?? 0),
    };
  }

  /** Cambió submódulo o tipo de permiso respecto al snapshot inicial */
  private hasMetadataChanges(): boolean {
    if (!this.ayuda.IdSubmoduloPermisoAyuda) {
      return false;
    }
    const raw = this.formAyuda.getRawValue();
    return (
      Number(raw.IdSubmodulo) !== this.initialMeta.IdSubmodulo
      || Number(raw.IdPermisoTipo) !== this.initialMeta.IdPermisoTipo
    );
  }

  private getCurrentHtmlByLang(): Record<string, string> {
    const htmlByLang: Record<string, string> = {};
    this.langs.forEach(({ id }) => {
      htmlByLang[id] = this.buildHtmlForLang(id);
    });
    return htmlByLang;
  }

  /** Idiomas cuyo HTML serializado difiere del snapshot (incluye renombrar secciones) */
  private getModifiedLangIdsToSave(): string[] {
    return getModifiedHelpLangIds(
      this.langs.map((l) => l.id),
      this.getCurrentHtmlByLang(),
      this.initialLangSnapshot,
    );
  }

  /** Idiomas a persistir: en creación todos con contenido; en edición solo modificados */
  private getLangIdsToSave(): string[] {
    const isCreate = !this.ayuda.IdSubmoduloPermisoAyuda;
    if (isCreate) {
      return this.langs
        .map((l) => l.id)
        .filter((id) => htmlToPlainText(this.buildHtmlForLang(id)).length > 0);
    }
    return this.getModifiedLangIdsToSave();
  }

  /** Limpia HTML legacy/Word al abrir el diálogo de edición */
  private sanitizeLoadedAyudaContent(): void {
    this.langs.forEach(({ id }) => {
      const clean = sanitizeHelpHtml(String(this.ayuda[id] ?? ''));
      this.ayuda[id] = clean;
      this.formAyuda.get(id)?.setValue(clean, { emitEvent: false });
    });
  }

  onLangIndexChange(index: number): void {
    const langId = this.langs[index]?.id ?? 'es';
    this.onLangTabSelect(langId);
  }

  onDocumentModeChange(mode: HelpDocumentUiMode): void {
    if (mode === this.documentMode) return;

    if (mode === 'sections') {
      this.langs.forEach(({ id }) => {
        this.sectionsByLang[id] = this.legacyHtmlToSections(this.formAyuda.get(id)?.value ?? '');
        if (htmlToPlainText(this.buildHtmlForLang(id)).length > 0) {
          this.markLangDirty(id);
        }
      });
      this.syncStructureFromLang('es');
    } else {
      this.langs.forEach(({ id }) => {
        const sections = this.sectionsByLang[id];
        if (sections?.length) {
          this.formAyuda.get(id)?.setValue(flattenHelpDocument(serializeHelpDocument(sections)));
          this.markLangDirty(id);
        }
      });
    }

    this.documentMode = mode;
  }

  onSectionsChange(langId: string, sections: HelpSection[]): void {
    this.sectionsByLang[langId] = sections.map((s) => ({ ...s }));
    this.markLangDirty(langId);
    if (langId === 'es') {
      this.syncStructureFromLang('es');
    }
  }

  /** Propaga id/título/orden del idioma base al resto (contenido por idioma) */
  private syncStructureFromLang(baseLang: string): void {
    const structure = this.sectionsByLang[baseLang];
    if (!structure?.length) return;

    this.langs.forEach(({ id }) => {
      if (id === baseLang) return;
      this.sectionsByLang[id] = alignSectionsToStructure(
        structure,
        this.sectionsByLang[id] ?? createDefaultSections(),
      );
      this.markLangDirty(id);
    });
  }

  private initSectionsFromAyuda(): void {
    let detectedSections = false;

    this.langs.forEach(({ id }) => {
      const html = (this.ayuda[id] as string) ?? '';
      const doc = parseHelpDocument(html);

      if (doc.mode === 'sections') {
        detectedSections = true;
        this.sectionsByLang[id] = doc.sections.map((s) => ({ ...s }));
      } else {
        this.sectionsByLang[id] = createDefaultSections();
      }
    });

    if (detectedSections) {
      this.documentMode = 'sections';
    }
  }

  private syncIdPermisoTipoEnabledFromSubmodulo(idSubmodulo: unknown): void {
    const ctrl = this.formAyuda.get('IdPermisoTipo');
    if (idSubmodulo === null || idSubmodulo === undefined || idSubmodulo === '') {
      ctrl?.disable({ emitEvent: false });
    } else {
      ctrl?.enable({ emitEvent: false });
    }
  }

  getData(): void {
    if (this.ayuda.IdSubmoduloPermisoAyuda) {
      this.tiposPermisoSubmodulo(this.ayuda.IdSubmodulo);
      this.filterSubmodulos(this.ayuda.idModulo);
    }

    this.database.TraerTiposPermisos().subscribe((tiposPermiso) => (this.tiposPermiso = tiposPermiso));
  }

  filterSubmodulos(idModulo: unknown): void {
    this.submodulosFiltered = this.submodulos.filter((sub) => sub.idModulo === idModulo);
  }

  tiposPermisoSubmodulo(idSubmodulo: unknown): void {
    this.database
      .tiposPermisosAyudas(Number(idSubmodulo))
      .subscribe(({ ayudas }) => (this.tiposPermisoAyudas = ayudas));
  }

  /**
   * Convierte HTML legacy en una lista de secciones (vacía o una sección con ese contenido).
   */
  private legacyHtmlToSections(html: string): HelpSection[] {
    const doc = parseHelpDocument(html);
    if (doc.mode === 'sections') {
      return doc.sections.map((s) => ({ ...s }));
    }
    if (!doc.legacyHtml || htmlToPlainText(doc.legacyHtml).length === 0) {
      return [];
    }
    const match = doc.legacyHtml.match(/<h2[^>]*>(.*?)<\/h2>/is);
    const title = match
      ? match[1].replace(/<[^>]*>/g, '').trim()
      : 'Sección 1';
    const id = uniqueSectionId(title, []);
    return [
      {
        id,
        title,
        content: doc.legacyHtml,
        order: 0,
      },
    ];
  }

  /** Vuelca el CKEditor al modelo antes de guardar o cambiar de pestaña de idioma */
  private flushSectionEditorsBeforeSave(): void {
    this.sectionEditors?.forEach((editor) => {
      if (!editor.langId) return;
      this.sectionsByLang[editor.langId] = editor.flushPendingContent();
    });
  }

  onLangTabSelect(langId: string): void {
    if (this.documentMode !== 'sections') {
      this.currentLangTabId = langId;
      return;
    }

    if (this.currentLangTabId && this.currentLangTabId !== langId) {
      const prevEditor = this.sectionEditors?.find((e) => e.langId === this.currentLangTabId);
      if (prevEditor) {
        this.sectionsByLang[this.currentLangTabId] = prevEditor.flushPendingContent();
      }
    }

    this.currentLangTabId = langId;
  }

  guardarCambios(): void {
    void this.saveChanges();
  }

  /** Guarda sin diálogos: idiomas con contenido (crear) o modificados (editar) */
  private async saveChanges(): Promise<void> {
    this.flushAndSanitizeModel();

    const langIds = this.getLangIdsToSave();
    const metaChanged = this.hasMetadataChanges();
    const isCreate = !this.ayuda.IdSubmoduloPermisoAyuda;

    if (langIds.length === 0 && (isCreate || !metaChanged)) {
      this.messageService.SwalMessage(
        'info',
        this.translations['help-sin-idiomas-modificados']
          ?? 'No hay cambios en ningún idioma para guardar.',
        false,
        3000,
      );
      return;
    }

    this.loaderService.showSpinner();
    try {
      await this.persistByLanguage(langIds, metaChanged);
    } catch {
      this.loaderService.hideSpinner();
      this.messageService.SwalMessage('error', this.translations['haOcurridoUnError']);
      return;
    }
    this.loaderService.hideSpinner();
    this.showSuccessAndClose();
  }

  /** Vuelca editores y sanitiza todo el modelo antes de guardar */
  private flushAndSanitizeModel(): void {
    if (this.documentMode === 'sections') {
      this.flushSectionEditorsBeforeSave();
      this.langs.forEach(({ id }) => {
        const sections = (this.sectionsByLang[id] ?? []).map((s) => ({
          ...s,
          content: sanitizeHelpHtmlFragment(s.content),
        }));
        this.sectionsByLang[id] = sections;
        this.formAyuda.get(id)?.setValue(
          sections.length ? serializeHelpDocument(sections) : '',
          { emitEvent: false },
        );
      });
      return;
    }

    this.langs.forEach(({ id }) => {
      const clean = sanitizeHelpHtml(String(this.formAyuda.get(id)?.value ?? ''));
      this.formAyuda.get(id)?.setValue(clean, { emitEvent: false });
    });
  }

  private buildHtmlForLang(langId: string): string {
    return buildHelpLangHtml(
      langId,
      this.documentMode,
      this.formAyuda.get(langId)?.value,
      this.sectionsByLang,
    );
  }

  /** Guarda uno o varios idiomas con peticiones independientes (evita payload grande) */
  private async persistByLanguage(langIds: string[], metaChanged = false): Promise<void> {
    const raw = this.formAyuda.getRawValue();
    const idSubmodulo = Number(raw.IdSubmodulo);
    const idPermisoTipo = Number(raw.IdPermisoTipo);
    const isCreate = !this.ayuda.IdSubmoduloPermisoAyuda;

    let helpId = Number(this.ayuda.IdSubmoduloPermisoAyuda ?? raw.IdSubmoduloPermisoAyuda);

    if (isCreate) {
      const bootstrapLang = langIds.includes('es') ? 'es' : langIds[0];
      const bootstrapHtml = this.buildHtmlForLang(bootstrapLang);
      const createPayload = {
        IdSubmodulo: idSubmodulo,
        IdPermisoTipo: idPermisoTipo,
        es: bootstrapLang === 'es' ? bootstrapHtml : '',
        en: bootstrapLang === 'en' ? bootstrapHtml : '',
        fr: bootstrapLang === 'fr' ? bootstrapHtml : '',
        ca: bootstrapLang === 'ca' ? bootstrapHtml : '',
        pv: bootstrapLang === 'pv' ? bootstrapHtml : '',
      };

      await firstValueFrom(this.database.crearAyuda(createPayload));
      helpId = await this.resolveHelpIdAfterCreate(idSubmodulo, idPermisoTipo);

      const remaining = langIds.filter((id) => id !== bootstrapLang);
      for (const langId of remaining) {
        await this.saveLanguageColumn(helpId, langId as Lang);
      }
      return;
    }

    if (metaChanged) {
      await firstValueFrom(this.database.modificarAyuda(this.buildFullAyudaPayload(helpId)));
      return;
    }

    for (const langId of langIds) {
      await this.saveLanguageColumn(helpId, langId as Lang);
    }
  }

  /** Payload completo para PUT /helps (metadatos + HTML de los 5 idiomas) */
  private buildFullAyudaPayload(helpId: number): Ayuda {
    const raw = this.formAyuda.getRawValue();
    return {
      IdSubmoduloPermisoAyuda: helpId,
      IdSubmodulo: Number(raw.IdSubmodulo),
      IdPermisoTipo: Number(raw.IdPermisoTipo),
      es: this.buildHtmlForLang('es'),
      en: this.buildHtmlForLang('en'),
      fr: this.buildHtmlForLang('fr'),
      ca: this.buildHtmlForLang('ca'),
      pv: this.buildHtmlForLang('pv'),
    };
  }

  private async saveLanguageColumn(helpId: number, lang: Lang): Promise<void> {
    const html = this.buildHtmlForLang(lang);
    await firstValueFrom(
      this.database.modificarAyudaLanguage({
        IdSubmoduloPermisoAyuda: helpId,
        lang,
        html,
      }),
    );
  }

  private async resolveHelpIdAfterCreate(idSubmodulo: number, idPermisoTipo: number): Promise<number> {
    const { ayudas } = await firstValueFrom(this.database.tiposPermisosAyudas(idSubmodulo));
    const row = (ayudas ?? []).find(
      (a: { IdPermisoTipo?: number; idPermisoTipo?: number; IdSubmoduloPermisoAyuda?: number }) =>
        Number(a.IdPermisoTipo ?? a.idPermisoTipo) === idPermisoTipo,
    );
    const id = Number(row?.IdSubmoduloPermisoAyuda);
    if (!id) {
      throw new Error('No se pudo resolver IdSubmoduloPermisoAyuda tras crear la ayuda');
    }
    return id;
  }

  private showSuccessAndClose(): void {
    this.messageService
      .SwalMessage('success', this.translations['mensaje_confirmacion'], false, 2000)
      .then(() => this.dialogRef.close({ created: true }));
  }

  onEditorReady(editor: any): void {
    attachHelpEditorPasteSanitizer(editor);
    editor.plugins.get('FileRepository').createUploadAdapter = (loader: any) =>
      new CkUploadAdapter(loader, this.http);
    queueMicrotask(() => editor.editing?.view?.focus());
  }

  onEditorContentChange(langId: string): void {
    this.markLangDirty(langId);
    this.formAyuda.get(langId)?.updateValueAndValidity({ emitEvent: false });
  }

  hasHelpCreated(IdPermisoTipo: number): boolean {
    return !!this.tiposPermisoAyudas.find((tipo) => tipo.IdPermisoTipo === IdPermisoTipo);
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  minimizarMaximizarHerramientas(): void {
    this.OcultarMostrar = !this.OcultarMostrar;
    this.IconoMinimizadoMaximizado = this.OcultarMostrar ? 'expand_more' : 'expand_less';
  }

  expandDialog(): void {
    this.expandedDialog = !this.expandedDialog;
    const size = this.expandedDialog
      ? { width: '100vw', height: '100vh' }
      : { width: '65%',  height: '85%' };
    this.dialogRef.updateSize(size.width, size.height);
  }
}
